<template>
  <div class="eventos-scope submain">
    <main class="cal-container">

      <!-- CABECERA -->
      <header class="cal-hero">
        <p class="cal-eyebrow">Agenda parroquial</p>
        <h1 class="cal-hero-title">Calendario de eventos</h1>
        <p class="cal-hero-sub">Consulta las celebraciones y actividades de la parroquia.</p>
      </header>

      <div class="cal-layout">

        <!-- SIDEBAR -->
        <aside class="cal-sidebar">

          <!-- Buscador -->
          <div class="cal-card cal-box">
            <h3 class="cal-box-title">Buscar</h3>
            <div class="cal-search">
              <span class="material-symbols-outlined">search</span>
              <input
                v-model.trim="search"
                type="search"
                placeholder="Buscar evento…"
                aria-label="Buscar evento"
              />
              <button v-if="search" type="button" aria-label="Limpiar búsqueda" @click="search = ''">
                <span class="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>

          <!-- Categorías -->
          <div class="cal-card cal-box">
            <div class="cal-box-head">
              <h3 class="cal-box-title">Categorías</h3>
              <button type="button" class="cal-link" @click="toggleAllCategories">
                {{ allCategoriesActive ? "Ninguna" : "Todas" }}
              </button>
            </div>

            <label
              v-for="c in categories"
              :key="c.key"
              class="cal-filter"
              :class="{ 'is-off': !activeCategories.includes(c.key) }"
              :style="{ '--c': c.color }"
            >
              <input v-model="activeCategories" type="checkbox" :value="c.key" class="cal-sr-only" />
              <span class="cal-check">
                <span class="material-symbols-outlined">check</span>
              </span>
              <span class="cal-filter-label">{{ c.label }}</span>
              <span class="cal-filter-count" :title="'Eventos en ' + monthNames[month]">
                {{ monthCounts[c.key] || 0 }}
              </span>
            </label>
          </div>

          <!-- Próximos eventos -->
          <div class="cal-card cal-box">
            <h3 class="cal-box-title">Próximos eventos</h3>

            <p v-if="loading" class="cal-muted">Cargando…</p>
            <p v-else-if="!upcoming.length" class="cal-muted">No hay próximos eventos.</p>

            <button
              v-for="e in upcoming"
              :key="e.id"
              type="button"
              class="cal-upcoming"
              :style="{ '--c': categoryColor(e.category) }"
              @click="goToEvent(e)"
            >
              <span class="cal-badge">
                <span class="cal-badge-month">{{ shortMonthNames[e.start.getMonth()] }}</span>
                <span class="cal-badge-day">{{ e.start.getDate() }}</span>
              </span>
              <span class="cal-upcoming-info">
                <span class="cal-upcoming-title">{{ e.title }}</span>
                <span class="cal-upcoming-meta">
                  {{ weekDaysLong[e.start.getDay()] }}
                  <template v-if="!e.allDay"> · {{ formatTime(e.start) }}</template>
                </span>
              </span>
            </button>
          </div>
        </aside>

        <!-- CALENDARIO -->
        <section class="cal-main">
          <div class="cal-card">

            <!-- TOOLBAR -->
            <div class="cal-toolbar">
              <div class="cal-nav">
                <button type="button" class="cal-icon-btn" aria-label="Mes anterior" @click="prevMonth">
                  <span class="material-symbols-outlined">chevron_left</span>
                </button>
                <h2 class="cal-month-title">
                  {{ monthNames[month] }} <span class="cal-year">{{ year }}</span>
                </h2>
                <button type="button" class="cal-icon-btn" aria-label="Mes siguiente" @click="nextMonth">
                  <span class="material-symbols-outlined">chevron_right</span>
                </button>
              </div>

              <div class="cal-actions">
                <button type="button" class="cal-btn" :disabled="isCurrentMonth" @click="goToday">
                  Hoy
                </button>
                <div class="cal-segmented" role="group" aria-label="Tipo de vista">
                  <button
                    type="button"
                    :class="{ 'is-active': view === 'month' }"
                    :aria-pressed="view === 'month'"
                    v-track:cambiar_vista_calendario="{ vista: 'mes' }"
                    @click="view = 'month'"
                  >
                    <span class="material-symbols-outlined">calendar_view_month</span>
                    <span class="cal-seg-label">Mes</span>
                  </button>
                  <button
                    type="button"
                    :class="{ 'is-active': view === 'list' }"
                    :aria-pressed="view === 'list'"
                    v-track:cambiar_vista_calendario="{ vista: 'lista' }"
                    @click="view = 'list'"
                  >
                    <span class="material-symbols-outlined">view_agenda</span>
                    <span class="cal-seg-label">Lista</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- ESTADOS -->
            <div v-if="loading" class="cal-state">
              <span class="cal-spinner"></span>
              <p>Cargando eventos…</p>
            </div>

            <div v-else-if="error" class="cal-state">
              <span class="material-symbols-outlined cal-state-icon">cloud_off</span>
              <p>No se pudieron cargar los eventos.</p>
              <button type="button" class="cal-btn" @click="cargarEventos">Reintentar</button>
            </div>

            <!-- VISTA MES -->
            <div v-else-if="view === 'month'" class="cal-month">
              <div class="cal-weekdays">
                <div v-for="(d, i) in weekDays" :key="d" :class="{ 'is-weekend': i > 4 }">
                  {{ d }}
                </div>
              </div>

              <div class="cal-grid">
                <button
                  v-for="cell in calendarCells"
                  :key="cell.key"
                  type="button"
                  class="cal-day"
                  :class="{
                    'is-outside': !cell.inMonth,
                    'is-today': cell.isToday,
                    'is-selected': isSelected(cell.date),
                    'is-weekend': cell.isWeekend,
                    'has-events': cell.events.length > 0
                  }"
                  :aria-label="dayAriaLabel(cell)"
                  @click="selectDate(cell.date)"
                >
                  <span class="cal-day-number">{{ cell.date.getDate() }}</span>

                  <span v-if="cell.events.length" class="cal-chips">
                    <span
                      v-for="e in cell.events.slice(0, 2)"
                      :key="e.id"
                      class="cal-chip"
                      :style="{ '--c': categoryColor(e.category) }"
                    >
                      <span v-if="!e.allDay" class="cal-chip-time">{{ formatTime(e.start) }}</span>
                      {{ e.title }}
                    </span>
                    <span v-if="cell.events.length > 2" class="cal-more">
                      +{{ cell.events.length - 2 }} más
                    </span>
                  </span>

                  <span v-if="cell.events.length" class="cal-dots">
                    <span
                      v-for="e in cell.events.slice(0, 3)"
                      :key="e.id"
                      class="cal-dot"
                      :style="{ background: categoryColor(e.category) }"
                    ></span>
                  </span>
                </button>
              </div>
            </div>

            <!-- VISTA LISTA -->
            <div v-else class="cal-list">
              <div v-if="!monthAgenda.length" class="cal-state">
                <span class="material-symbols-outlined cal-state-icon">event_busy</span>
                <p>No hay eventos en {{ monthNames[month].toLowerCase() }}{{ hasFilters ? " con estos filtros" : "" }}.</p>
              </div>

              <div
                v-for="group in monthAgenda"
                :key="group.key"
                class="cal-list-group"
                :class="{ 'is-today': group.isToday }"
              >
                <div class="cal-list-date">
                  <span class="cal-list-dow">{{ weekDays[(group.date.getDay() + 6) % 7] }}</span>
                  <span class="cal-list-num">{{ group.date.getDate() }}</span>
                </div>
                <div class="cal-list-items">
                  <button
                    v-for="e in group.events"
                    :key="e.id"
                    type="button"
                    class="cal-list-item"
                    :style="{ '--c': categoryColor(e.category) }"
                    @click="selectDate(group.date)"
                  >
                    <span class="cal-list-time">{{ e.allDay ? "Todo el día" : formatTime(e.start) }}</span>
                    <span class="cal-list-title">{{ e.title }}</span>
                    <span v-if="e.location" class="cal-list-loc">
                      <span class="material-symbols-outlined">location_on</span>{{ e.location }}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>

    <!-- PANEL DETALLE -->
    <transition name="cal-fade">
      <div v-if="showPanel" class="cal-overlay" @click="closePanel"></div>
    </transition>

    <transition name="cal-slide">
      <aside
        v-if="showPanel && selectedDate"
        class="cal-drawer"
        role="dialog"
        aria-modal="true"
        :aria-label="'Eventos del ' + longDate(selectedDate)"
      >
        <header class="cal-drawer-head">
          <div>
            <p class="cal-drawer-dow">{{ weekDaysLong[selectedDate.getDay()] }}</p>
            <h3 class="cal-drawer-title">
              {{ selectedDate.getDate() }} de {{ monthNames[selectedDate.getMonth()].toLowerCase() }}
              <span class="cal-year">{{ selectedDate.getFullYear() }}</span>
            </h3>
          </div>
          <button ref="closeBtn" type="button" class="cal-icon-btn" aria-label="Cerrar" @click="closePanel">
            <span class="material-symbols-outlined">close</span>
          </button>
        </header>

        <div class="cal-drawer-nav">
          <button type="button" class="cal-link" @click="shiftSelected(-1)">
            <span class="material-symbols-outlined">chevron_left</span> Día anterior
          </button>
          <button type="button" class="cal-link" @click="shiftSelected(1)">
            Día siguiente <span class="material-symbols-outlined">chevron_right</span>
          </button>
        </div>

        <div class="cal-drawer-body">
          <template v-if="selectedEvents.length">
            <article
              v-for="e in selectedEvents"
              :key="e.id"
              class="cal-event-card"
              :style="{ '--c': categoryColor(e.category) }"
            >
              <span class="cal-tag">{{ categoryLabel(e.category) }}</span>
              <h4 class="cal-event-title">{{ e.title }}</h4>

              <p class="cal-meta">
                <span class="material-symbols-outlined">schedule</span>
                {{ timeRange(e) }}
              </p>
              <p v-if="e.location" class="cal-meta">
                <span class="material-symbols-outlined">location_on</span>
                {{ e.location }}
              </p>
              <p v-if="e.description" class="cal-desc">{{ e.description }}</p>

              <a
                v-track:anadir_calendario="{ evento: e.title, origen: 'calendario' }"
                :href="googleCalendarLink(e)"
                target="_blank"
                rel="noopener"
                class="cal-add"
              >
                <span class="material-symbols-outlined">event_available</span>
                Añadir a mi calendario
              </a>

              <botones-compartir
                class="cal-share"
                compacto
                :titulo="e.title"
                :texto="textoEvento(e)"
                :ruta="rutaEvento(e)"
                tipo="evento"
              />
            </article>
          </template>

          <div v-else class="cal-state">
            <span class="material-symbols-outlined cal-state-icon">event_busy</span>
            <p>No hay eventos este día.</p>
          </div>
        </div>
      </aside>
    </transition>
  </div>
</template>

<script>
import axios from "@/directives/axios.js";
import BotonesCompartir from "@/components/compartir/BotonesCompartir.vue";

import {
  CATEGORIES,
  MONTH_NAMES,
  MONTH_SHORT,
  WEEKDAYS_LONG,
  normalizeText,
  normalizeEvent,
  getCategory,
  formatTime,
  longDate,
  timeRange,
  googleCalendarLink,
  toISODate,
  rutaEvento,
  textoEvento,
} from "@/directives/eventos.js";
import { track, trackBusqueda } from "@/directives/analitica.js";

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function dateKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function sameDay(a, b) {
  return !!a && !!b && dateKey(a) === dateKey(b);
}

export default {
  name: "EventosCalendario",
  components: { BotonesCompartir },
  data() {
    const today = new Date();
    return {
      year: today.getFullYear(),
      month: today.getMonth(),
      events: [],
      loading: true,
      error: false,
      selectedDate: null,
      showPanel: false,
      view: typeof window !== "undefined" && window.innerWidth < 640 ? "list" : "month",
      search: "",
      categories: CATEGORIES,
      activeCategories: CATEGORIES.map((c) => c.key),
      weekDays: ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
      weekDaysLong: WEEKDAYS_LONG,
      monthNames: MONTH_NAMES,
      shortMonthNames: MONTH_SHORT,
    };
  },
  computed: {
    // Eventos del API convertidos a un formato cómodo y ordenados por fecha
    normalizedEvents() {
      return this.events
        .map(normalizeEvent)
        .filter(Boolean)
        .sort((a, b) => a.start - b.start);
    },
    hasFilters() {
      return !!this.search || !this.allCategoriesActive;
    },
    filteredEvents() {
      const q = normalizeText(this.search);
      return this.normalizedEvents.filter(
        (e) => this.activeCategories.includes(e.category) && (!q || e.searchText.includes(q))
      );
    },
    // Mapa día -> eventos (los eventos de varios días aparecen en todos sus días)
    eventsByDay() {
      const map = {};
      this.filteredEvents.forEach((e) => {
        let last = startOfDay(e.start);
        if (e.end && e.end > e.start) {
          // En eventos de día completo el fin es exclusivo; en los demás, si acaba a medianoche no cuenta ese día
          const endAdj = new Date(e.end.getTime() - 1);
          last = startOfDay(endAdj);
        }
        let cursor = startOfDay(e.start);
        for (let i = 0; cursor <= last && i < 62; i++) {
          const key = dateKey(cursor);
          (map[key] = map[key] || []).push(e);
          cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
        }
      });
      Object.values(map).forEach((list) =>
        list.sort((a, b) => (a.allDay === b.allDay ? a.start - b.start : a.allDay ? -1 : 1))
      );
      return map;
    },
    calendarCells() {
      const first = new Date(this.year, this.month, 1);
      const offset = (first.getDay() + 6) % 7;
      const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
      const total = Math.ceil((offset + daysInMonth) / 7) * 7;
      const today = new Date();
      const cells = [];
      for (let i = 0; i < total; i++) {
        const date = new Date(this.year, this.month, 1 - offset + i);
        const dow = (date.getDay() + 6) % 7;
        cells.push({
          key: dateKey(date),
          date,
          inMonth: date.getMonth() === this.month,
          isToday: sameDay(date, today),
          isWeekend: dow > 4,
          events: this.eventsByDay[dateKey(date)] || [],
        });
      }
      return cells;
    },
    monthAgenda() {
      const daysInMonth = new Date(this.year, this.month + 1, 0).getDate();
      const today = new Date();
      const groups = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(this.year, this.month, d);
        const events = this.eventsByDay[dateKey(date)];
        if (events && events.length) {
          groups.push({ key: dateKey(date), date, events, isToday: sameDay(date, today) });
        }
      }
      return groups;
    },
    // Contador por categoría del mes visible (sin aplicar el filtro de categorías)
    monthCounts() {
      const counts = {};
      const q = normalizeText(this.search);
      this.normalizedEvents.forEach((e) => {
        if (e.start.getFullYear() !== this.year || e.start.getMonth() !== this.month) return;
        if (q && !e.searchText.includes(q)) return;
        counts[e.category] = (counts[e.category] || 0) + 1;
      });
      return counts;
    },
    upcoming() {
      const now = new Date();
      const todayStart = startOfDay(now);
      return this.filteredEvents
        .filter((e) => {
          const end = e.end || e.start;
          return e.allDay ? end >= todayStart : end >= now;
        })
        .slice(0, 5);
    },
    selectedEvents() {
      return this.selectedDate ? this.eventsByDay[dateKey(this.selectedDate)] || [] : [];
    },
    isCurrentMonth() {
      const t = new Date();
      return t.getMonth() === this.month && t.getFullYear() === this.year;
    },
    allCategoriesActive() {
      return this.activeCategories.length === this.categories.length;
    },
  },
  watch: {
    "$route.query.fecha"() {
      this.abrirFechaDeRuta();
    },
    showPanel(open) {
      document.body.style.overflow = open ? "hidden" : "";
      if (open) this.$nextTick(() => this.$refs.closeBtn && this.$refs.closeBtn.focus());
    },
    search(texto) {
      trackBusqueda("calendario", texto);
    },
  },
  methods: {
    categoryColor(key) {
      return getCategory(key).color;
    },
    categoryLabel(key) {
      return getCategory(key).label;
    },
    toggleAllCategories() {
      this.activeCategories = this.allCategoriesActive ? [] : this.categories.map((c) => c.key);
    },
    formatTime,
    longDate,
    timeRange,
    googleCalendarLink,
    rutaEvento,
    textoEvento,
    dayAriaLabel(cell) {
      const n = cell.events.length;
      return `${longDate(cell.date)}${n ? `, ${n} evento${n > 1 ? "s" : ""}` : ", sin eventos"}`;
    },
    // ?fecha=YYYY-MM-DD abre directamente ese día (enlace desde la portada)
    abrirFechaDeRuta() {
      const m = String(this.$route.query.fecha || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) this.selectDate(new Date(+m[1], +m[2] - 1, +m[3]));
    },
    isSelected(date) {
      return this.showPanel && sameDay(date, this.selectedDate);
    },
    selectDate(date) {
      this.selectedDate = startOfDay(date);
      this.year = date.getFullYear();
      this.month = date.getMonth();
      this.showPanel = true;
      track("ver_dia_calendario", {
        fecha: toISODate(this.selectedDate),
        eventos: this.selectedEvents.length,
        evento: this.selectedEvents.map((e) => e.title).join(" · ").slice(0, 100) || undefined,
      });
    },
    shiftSelected(delta) {
      const d = this.selectedDate;
      this.selectDate(new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta));
    },
    goToEvent(e) {
      this.selectDate(e.start);
    },
    closePanel() {
      this.showPanel = false;
    },
    goToday() {
      const t = new Date();
      this.month = t.getMonth();
      this.year = t.getFullYear();
    },
    prevMonth() {
      if (this.month === 0) {
        this.month = 11;
        this.year--;
      } else {
        this.month--;
      }
    },
    nextMonth() {
      if (this.month === 11) {
        this.month = 0;
        this.year++;
      } else {
        this.month++;
      }
    },
    onKeydown(ev) {
      if (!this.showPanel) return;
      if (ev.key === "Escape") this.closePanel();
      else if (ev.key === "ArrowLeft") this.shiftSelected(-1);
      else if (ev.key === "ArrowRight") this.shiftSelected(1);
    },
    async cargarEventos() {
      this.loading = true;
      this.error = false;
      try {
        const res = await axios.get("/eventos");
        if (res.data && res.data.success) {
          this.events = res.data.eventos || [];
        } else {
          this.error = true;
        }
      } catch (err) {
        console.error("Error cargando eventos:", err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.cargarEventos();
    this.abrirFechaDeRuta();
    window.addEventListener("keydown", this.onKeydown);
  },
  activated() {
    this.abrirFechaDeRuta();
  },
  deactivated() {
    this.showPanel = false;
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
    document.body.style.overflow = "";
  },
};
</script>

<!-- Tokens de color: claro por defecto y oscuro cuando <html> tiene la clase .dark -->
<style>
.eventos-scope {
  --cal-surface: #ffffff;
  --cal-surface-2: #f8fafc;
  --cal-border: #e2e8f0;
  --cal-grid-line: #eef2f6;
  --cal-text: #0f172a;
  --cal-muted: #64748b;
  --cal-faint: #cbd5e1;
  --cal-accent: #2563eb;
  --cal-accent-contrast: #ffffff;
  --cal-accent-soft: #eff6ff;
  --cal-hover: #f1f5f9;
  --cal-outside: #fafbfc;
  --cal-weekend: #fcfcfd;
  --cal-overlay: rgba(15, 23, 42, 0.35);
  --cal-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.06);
  --cal-chip-mix: 14%;
}

.dark .eventos-scope {
  --cal-surface: #1e293b;
  --cal-surface-2: #0f172a;
  --cal-border: #334155;
  --cal-grid-line: #2a3649;
  --cal-text: #f1f5f9;
  --cal-muted: #94a3b8;
  --cal-faint: #475569;
  --cal-accent: #60a5fa;
  --cal-accent-contrast: #0f172a;
  --cal-accent-soft: rgba(96, 165, 250, 0.14);
  --cal-hover: #263449;
  --cal-outside: #192334;
  --cal-weekend: #1b2638;
  --cal-overlay: rgba(2, 6, 23, 0.65);
  --cal-shadow: 0 1px 2px rgba(0, 0, 0, 0.3), 0 10px 30px rgba(0, 0, 0, 0.35);
  --cal-chip-mix: 24%;
  color-scheme: dark;
}
</style>

<style scoped>
/* ---------- Base ---------- */
.cal-container {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 20px 64px;
  color: var(--cal-text);
}

.cal-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.cal-muted {
  color: var(--cal-muted);
  font-size: 0.9rem;
  margin: 0;
}

button {
  font: inherit;
  color: inherit;
  background: none;
  border: 0;
  cursor: pointer;
}

button:focus-visible,
a:focus-visible,
.cal-filter:focus-within {
  outline: 2px solid var(--cal-accent);
  outline-offset: 2px;
}

.cal-card {
  background: var(--cal-surface);
  border: 1px solid var(--cal-border);
  border-radius: 16px;
  box-shadow: var(--cal-shadow);
}

/* ---------- Cabecera ---------- */
.cal-hero {
  margin-bottom: 28px;
}

.cal-eyebrow {
  margin: 0 0 6px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--cal-accent);
}

.cal-hero-title {
  margin: 0;
  font-family: "Cinzel", Georgia, serif;
  font-size: clamp(1.75rem, 3.2vw, 2.5rem);
  font-weight: 700;
  color: var(--cal-text);
}

.cal-hero-sub {
  margin: 6px 0 0;
  color: var(--cal-muted);
}

/* ---------- Layout ---------- */
.cal-layout {
  display: grid;
  grid-template-columns: 290px minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.cal-sidebar {
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 110px;
}

/* ---------- Sidebar ---------- */
.cal-box {
  padding: 18px;
}

.cal-box-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.cal-box-title {
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--cal-muted);
}

.cal-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--cal-accent);
}

.cal-link .material-symbols-outlined {
  font-size: 18px;
  color: var(--cal-accent);
}

.cal-link:hover {
  text-decoration: underline;
}

.cal-search {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 10px;
  border: 1px solid var(--cal-border);
  border-radius: 10px;
  background: var(--cal-surface-2);
  transition: border-color 0.15s, box-shadow 0.15s;
}

.cal-search:focus-within {
  border-color: var(--cal-accent);
  box-shadow: 0 0 0 3px var(--cal-accent-soft);
}

.cal-search .material-symbols-outlined {
  font-size: 20px;
  color: var(--cal-muted);
}

.cal-search input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: var(--cal-text);
  font-size: 0.9rem;
}

.cal-search input::placeholder {
  color: var(--cal-muted);
}

.cal-search input::-webkit-search-cancel-button {
  display: none;
}

.cal-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  margin: 0 -8px;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, opacity 0.15s;
}

.cal-filter:hover {
  background: var(--cal-hover);
}

.cal-filter.is-off {
  opacity: 0.55;
}

.cal-check {
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 2px solid var(--c);
  background: var(--c);
  transition: background 0.15s;
}

.cal-check .material-symbols-outlined {
  font-size: 14px;
  font-weight: 700;
  color: #fff;
}

.cal-filter.is-off .cal-check {
  background: transparent;
}

.cal-filter.is-off .cal-check .material-symbols-outlined {
  visibility: hidden;
}

.cal-filter-label {
  flex: 1;
  font-size: 0.92rem;
  color: var(--cal-text);
}

.cal-filter-count {
  min-width: 24px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--cal-surface-2);
  border: 1px solid var(--cal-border);
  font-size: 0.75rem;
  text-align: center;
  color: var(--cal-muted);
}

.cal-upcoming {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  margin: 0 -8px;
  width: calc(100% + 16px);
  border-radius: 10px;
  text-align: left;
  transition: background 0.15s;
}

.cal-upcoming:hover {
  background: var(--cal-hover);
}

.cal-badge {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 50px;
  border-radius: 10px;
  background: var(--cal-surface-2);
  border: 1px solid var(--cal-border);
  border-top: 4px solid var(--c);
}

.cal-badge-month {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--cal-muted);
}

.cal-badge-day {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--cal-text);
}

.cal-upcoming-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.cal-upcoming-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--cal-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal-upcoming-meta {
  font-size: 0.78rem;
  color: var(--cal-muted);
}

/* ---------- Toolbar ---------- */
.cal-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--cal-border);
}

.cal-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cal-month-title {
  min-width: 190px;
  margin: 0;
  text-align: center;
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--cal-text);
}

.cal-year {
  font-weight: 400;
  color: var(--cal-muted);
}

.cal-icon-btn {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid var(--cal-border);
  background: var(--cal-surface);
  transition: background 0.15s;
}

.cal-icon-btn .material-symbols-outlined {
  font-size: 22px;
  color: var(--cal-text);
}

.cal-icon-btn:hover {
  background: var(--cal-hover);
}

.cal-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cal-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 10px;
  background: var(--cal-accent);
  color: var(--cal-accent-contrast);
  font-size: 0.875rem;
  font-weight: 600;
  transition: filter 0.15s, opacity 0.15s;
}

.cal-btn:hover:not(:disabled) {
  filter: brightness(1.08);
}

.cal-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.cal-segmented {
  display: flex;
  padding: 3px;
  border-radius: 10px;
  background: var(--cal-surface-2);
  border: 1px solid var(--cal-border);
}

.cal-segmented button {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--cal-muted);
}

.cal-segmented button .material-symbols-outlined,
.cal-segmented button .cal-seg-label {
  color: inherit;
}

.cal-segmented button .material-symbols-outlined {
  font-size: 18px;
}

.cal-segmented button.is-active {
  background: var(--cal-surface);
  color: var(--cal-text);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

/* ---------- Estados ---------- */
.cal-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 64px 20px;
  text-align: center;
  color: var(--cal-muted);
}

.cal-state p {
  margin: 0;
  color: var(--cal-muted);
}

.cal-state .cal-state-icon {
  font-size: 40px;
  color: var(--cal-faint);
}

.cal-spinner {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 3px solid var(--cal-border);
  border-top-color: var(--cal-accent);
  animation: cal-spin 0.8s linear infinite;
}

@keyframes cal-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ---------- Vista mes ---------- */
.cal-weekdays,
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.cal-weekdays > div {
  padding: 10px 0;
  text-align: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cal-muted);
  background: var(--cal-surface-2);
  border-bottom: 1px solid var(--cal-border);
}

.cal-weekdays > div.is-weekend {
  color: var(--cal-accent);
}

.cal-day {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  min-height: 112px;
  padding: 6px;
  text-align: left;
  border-right: 1px solid var(--cal-grid-line);
  border-bottom: 1px solid var(--cal-grid-line);
  background: var(--cal-surface);
  transition: background 0.15s;
}

.cal-day:nth-child(7n) {
  border-right: 0;
}

.cal-day:nth-last-child(-n + 7) {
  border-bottom: 0;
}

.cal-day.is-weekend {
  background: var(--cal-weekend);
}

.cal-day.is-outside {
  background: var(--cal-outside);
}

.cal-day:hover {
  background: var(--cal-hover);
}

.cal-day.is-selected {
  background: var(--cal-accent-soft);
  box-shadow: inset 0 0 0 2px var(--cal-accent);
}

.cal-day:focus-visible {
  outline-offset: -2px;
  z-index: 1;
}

.cal-day-number {
  display: grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--cal-text);
}

.cal-day.is-outside .cal-day-number {
  color: var(--cal-faint);
}

.cal-day.is-today .cal-day-number {
  background: var(--cal-accent);
  color: var(--cal-accent-contrast);
}

.cal-chips {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.cal-chip {
  display: block;
  padding: 2px 6px;
  border-radius: 5px;
  border-left: 3px solid var(--c);
  background: color-mix(in srgb, var(--c) var(--cal-chip-mix), transparent);
  font-size: 0.72rem;
  font-weight: 500;
  line-height: 1.35;
  color: var(--cal-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cal-day.is-outside .cal-chip {
  opacity: 0.6;
}

.cal-chip-time {
  font-weight: 700;
  margin-right: 3px;
  color: var(--cal-text);
}

.cal-more {
  padding-left: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--cal-muted);
}

.cal-dots {
  display: none;
  justify-content: center;
  gap: 3px;
}

.cal-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

/* ---------- Vista lista ---------- */
.cal-list {
  padding: 8px 20px 20px;
}

.cal-list-group {
  display: flex;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--cal-grid-line);
}

.cal-list-group:last-child {
  border-bottom: 0;
}

.cal-list-date {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 48px;
  padding-top: 2px;
}

.cal-list-dow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cal-muted);
}

.cal-list-num {
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--cal-text);
}

.cal-list-group.is-today .cal-list-num {
  background: var(--cal-accent);
  color: var(--cal-accent-contrast);
}

.cal-list-items {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.cal-list-item {
  display: grid;
  grid-template-columns: 90px minmax(0, 1fr);
  column-gap: 12px;
  row-gap: 2px;
  align-items: baseline;
  padding: 10px 12px;
  border-radius: 10px;
  border-left: 4px solid var(--c);
  background: color-mix(in srgb, var(--c) 8%, var(--cal-surface));
  text-align: left;
  transition: transform 0.15s, background 0.15s;
}

.cal-list-item:hover {
  transform: translateX(3px);
  background: color-mix(in srgb, var(--c) 14%, var(--cal-surface));
}

.cal-list-time {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--cal-muted);
}

.cal-list-title {
  font-weight: 600;
  color: var(--cal-text);
}

.cal-list-loc {
  grid-column: 2;
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.8rem;
  color: var(--cal-muted);
}

.cal-list-loc .material-symbols-outlined {
  font-size: 16px;
  color: var(--cal-muted);
}

/* ---------- Panel detalle ---------- */
.cal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: var(--cal-overlay);
  backdrop-filter: blur(2px);
}

.cal-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1101;
  display: flex;
  flex-direction: column;
  width: min(420px, 100vw);
  background: var(--cal-surface);
  border-left: 1px solid var(--cal-border);
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.18);
  color: var(--cal-text);
}

.cal-drawer-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 22px 22px 12px;
}

.cal-drawer-dow {
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--cal-accent);
}

.cal-drawer-title {
  margin: 2px 0 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--cal-text);
}

.cal-drawer-nav {
  display: flex;
  justify-content: space-between;
  padding: 0 22px 12px;
  border-bottom: 1px solid var(--cal-border);
}

.cal-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 18px 22px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cal-event-card {
  padding: 16px;
  border-radius: 12px;
  border: 1px solid var(--cal-border);
  border-left: 4px solid var(--c);
  background: var(--cal-surface-2);
}

.cal-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: color-mix(in srgb, var(--c) var(--cal-chip-mix), transparent);
  color: var(--cal-text);
}

.cal-event-title {
  margin: 8px 0 8px;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--cal-text);
}

.cal-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 4px;
  font-size: 0.88rem;
  color: var(--cal-muted);
}

.cal-meta .material-symbols-outlined {
  font-size: 18px;
  color: var(--cal-muted);
}

.cal-desc {
  margin: 10px 0 0;
  font-size: 0.9rem;
  line-height: 1.55;
  white-space: pre-line;
  color: var(--cal-text);
}

.cal-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 12px;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--cal-accent);
  text-decoration: none;
}

.cal-add .material-symbols-outlined {
  font-size: 18px;
  color: var(--cal-accent);
}

.cal-add:hover {
  text-decoration: underline;
}

.cal-share {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px dashed var(--sd-border);
}

/* Transiciones */
.cal-fade-enter-active,
.cal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.cal-fade-enter,
.cal-fade-leave-to {
  opacity: 0;
}

.cal-slide-enter-active,
.cal-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.cal-slide-enter,
.cal-slide-leave-to {
  transform: translateX(100%);
}

/* ---------- Responsive ---------- */
@media (max-width: 1024px) {
  .cal-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .cal-main {
    order: 1;
  }

  .cal-sidebar {
    order: 2;
    position: static;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }
}

@media (max-width: 640px) {
  .cal-container {
    padding: 0 12px 48px;
  }

  .cal-toolbar {
    padding: 12px;
  }

  .cal-nav {
    width: 100%;
    justify-content: space-between;
  }

  .cal-month-title {
    min-width: 0;
    font-size: 1.15rem;
  }

  .cal-actions {
    width: 100%;
    justify-content: space-between;
  }

  .cal-day {
    min-height: 58px;
    align-items: center;
    padding: 4px 2px;
  }

  .cal-chips {
    display: none;
  }

  .cal-dots {
    display: flex;
  }

  .cal-list {
    padding: 4px 12px 12px;
  }

  .cal-list-item {
    grid-template-columns: minmax(0, 1fr);
  }

  .cal-list-loc {
    grid-column: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cal-slide-enter-active,
  .cal-slide-leave-active,
  .cal-fade-enter-active,
  .cal-fade-leave-active,
  .cal-list-item {
    transition: none;
  }
}
</style>
